import type { DataItem } from '@pn/core/domain/data';
import type { IntelReport } from '@pn/core/domain/intel/intel-report';
import { pnMappings } from '@pn/resources/pn-mappings';
import { apiDataItemMapper } from '@pn/services/api/data/apiDataItemMapper';
import { apiIntelReportMapper } from '@pn/services/api/intel-report/apiIntelReportsMapper';
import { pnApiClient } from '@pn/services/api/pnApiClient';
import { isEmpty } from 'lodash-es';
import type { ApiIntelReport } from './types';

const requestIntelReports = async (userId?: string) => {
  let apiIntelReports: ApiIntelReport[] = [];
  if (isEmpty(userId)) {
    apiIntelReports = await pnApiClient.request<ApiIntelReport[]>({
      url: `v1/market_intel/intel_reports`,
    });
  } else {
    // can do the admin version of the endpoint here? unsure
  }

  return apiIntelReports.map((report) =>
    apiIntelReportMapper.toDomainIntelReport(report)
  );
};

const createIntelReport = async (report: IntelReport) => {
  const { uuid } = await pnApiClient.request<{ uuid: string }>({
    method: 'POST',
    url: 'v1/market_intel/intel_reports',
    payload: apiIntelReportMapper.toTargetIntelReport(report),
  });

  return uuid;
};

const updateIntelReport = async (report: IntelReport) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v1/market_intel/intel_reports/${report.id}`,
    payload: apiIntelReportMapper.toTargetIntelReport(report),
  });
};

const deleteIntelReport = async (reportId: IntelReport['id']) => {
  return pnApiClient.request<void>({
    method: 'DELETE',
    url: `v1/market_intel/intel_reports/${reportId}`,
  });
};

const updateIntelReportIsPinned = async (
  reportId: IntelReport['id'],
  isPinned: boolean
) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v1/market_intel/intel_reports/${reportId}/is_pinned`,
    payload: {
      is_pinned: isPinned,
    },
  });
};

const updateIntelReportEmailSchedule = async (
  reportId: IntelReport['id'],
  emailSchedule: IntelReport['emailSchedule'] | null
) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v1/market_intel/intel_reports/${reportId}/email_schedule`,
    payload: {
      email_schedule: emailSchedule,
    },
  });
};

/**
 * The report data endpoints take the processed report filters as a loosely
 * typed payload and return untyped responses.
 */
const getWellsYearOverYear = async (payload: Record<string, unknown>) => {
  return pnApiClient.request<any>({
    url: `v2/intel/report/wells_yoy`,
    method: 'POST',
    payload,
  });
};

const getMonthsProduction = async (
  payload: Record<string, unknown>
): Promise<DataItem[]> => {
  const response = await pnApiClient.request<any[]>({
    url: `v2/intel/report/months_production`,
    method: 'POST',
    payload,
  });

  return response.map(apiDataItemMapper(pnMappings['wells']).toDomainDataItem);
};

export const apiIntelReportsProvider = {
  getIntelReports: requestIntelReports,
  createIntelReport,
  updateIntelReport,
  deleteIntelReport,
  updateIntelReportIsPinned,
  updateIntelReportEmailSchedule,
  getWellsYearOverYear,
  getMonthsProduction,
};
